import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { api } from '../../lib/api'
import { formatPrice, steamStoreUrl } from '../../lib/format'
import type { GameDetail, GameResponse } from '../../lib/types'
import { categoryLabel } from './humanize'
import { Badge, Drawer, GameArt, Spinner } from '../ui'

interface GameDetailDrawerProps {
  appid: number | null
  onClose: () => void
}

function VerdictBadge({ verdict }: { verdict: GameDetail['quality_verdict'] }) {
  if (verdict === 'pass') return <Badge variant="success">Quality: solid</Badge>
  if (verdict === 'uncertain') return <Badge variant="warning">Quality: uncertain</Badge>
  return null
}

export function GameDetailDrawer({ appid, onClose }: GameDetailDrawerProps) {
  const [game, setGame] = useState<GameDetail | null>(null)
  const [loading, setLoading] = useState(appid != null)

  // Closing clears the loaded detail; a new appid shows the spinner at once.
  // Adjusted while rendering (guarded by the previous value, per the React
  // docs) so the fetch effect never sets state synchronously.
  const [prevAppid, setPrevAppid] = useState(appid)
  if (prevAppid !== appid) {
    setPrevAppid(appid)
    if (appid == null) {
      setGame(null)
    } else {
      setLoading(true)
    }
  }

  useEffect(() => {
    if (appid == null) return
    let cancelled = false
    api
      .get<GameResponse>(`/games/${appid}`)
      .then((data) => {
        if (!cancelled) setGame(data.game)
      })
      .catch(() => {
        if (!cancelled) setGame(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appid])

  return (
    <Drawer open={appid != null} onClose={onClose} title={game?.name ?? (appid != null ? `App ${appid}` : '')}>
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : !game ? (
        <p className="text-sm text-text-muted">No catalog details available.</p>
      ) : (
        <div className="space-y-4">
          {/* Steam header art is 460×215 — render at its native aspect so the
              detail surface never crops the cover. */}
          <GameArt appid={game.appid} src={game.header_image} name={game.name} className="aspect-[460/215] h-auto w-full" />

          <div className="flex flex-wrap items-center gap-2">
            <VerdictBadge verdict={game.quality_verdict} />
            {game.price && (
              <Badge>
                <span className="tabular-nums">{formatPrice(game.price)}</span>
              </Badge>
            )}
          </div>

          {/* Everything the payload carries, in the standard chip language —
              the detail surface must outshine the card, not trail it. */}
          {game.store_categories.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-medium tracking-wide text-text-muted">Plays as</h4>
              <div className="flex flex-wrap gap-1.5">
                {game.store_categories.map((c) => (
                  <Badge key={c}>{categoryLabel(c)}</Badge>
                ))}
              </div>
            </div>
          )}

          {game.tags.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-medium tracking-wide text-text-muted">Tags</h4>
              <div className="flex flex-wrap gap-1.5">
                {game.tags.slice(0, 8).map((t, i) => (
                  <Badge key={`${t.tag}-${i}`}>{t.tag}</Badge>
                ))}
              </div>
            </div>
          )}

          <a
            href={steamStoreUrl(game.appid)}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-sm font-medium text-ember-300 hover:text-ember-400"
          >
            View on Steam <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            <span className="sr-only"> (opens in new tab)</span>
          </a>
        </div>
      )}
    </Drawer>
  )
}
